
import { useParams, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useJobList } from "@/hooks/useJobList";
import { JobOverview } from "@/components/jobs/detail/JobOverview";
import { MatchingCandidates } from "@/components/jobs/detail/MatchingCandidates";
import { CandidateSourceAction } from "@/components/jobs/detail/CandidateSourceAction";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApplicationsTab } from "@/components/jobs/detail/ApplicationsTab";
import { ActivityTab } from "@/components/jobs/detail/ActivityTab";
import { AnalyticsTab } from "@/components/jobs/detail/AnalyticsTab";
import { InterviewManagementTab } from "@/components/jobs/detail/InterviewManagementTab";
import { Job } from "@/components/jobs/types";
import { trackJobViewed } from "@/utils/analytics/jobTracking";

const JobDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const { data: jobs = [] } = useJobList();
  const job = jobs.find(j => j.id === id) as Job | undefined;

  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash === "matching-candidates" || hash === "applications" || hash === "activity" || hash === "analytics" || hash === "overview-edit" || hash === "interviews") {
      if (hash === "overview-edit") {
        setActiveTab("overview"); 
      } else {
        setActiveTab(hash);
      }
    } else {
      setActiveTab("overview");
    }
  }, [location.hash]);

  // Track job view when job is loaded
  useEffect(() => {
    if (job && id) {
      trackJobViewed({
        job_id: id,
        title: job.title,
        company: job.company,
        view_context: 'detail_page'
      });
    }
  }, [job, id]);

  // Track tab changes for job views
  useEffect(() => {
    if (job && id && activeTab !== "overview") {
      trackJobViewed({
        job_id: id,
        title: job.title,
        company: job.company,
        view_context: `tab_${activeTab}`
      });
    }
  }, [activeTab, job, id]);

  if (!job) {
    return (
      <div className="text-center py-10">
        <p className="text-muted-foreground">Job not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 md:grid-cols-6 mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="matching-candidates">Matching Candidates</TabsTrigger>
          <TabsTrigger value="applications">Applications</TabsTrigger>
          <TabsTrigger value="interviews">Interviews</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <JobOverview job={job} />
        </TabsContent>

        <TabsContent value="matching-candidates">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <MatchingCandidates job={job} />
            </div>
            <div className="lg:col-span-1">
              <CandidateSourceAction job={job} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="applications">
          <ApplicationsTab job={job} />
        </TabsContent>

        <TabsContent value="interviews">
          <InterviewManagementTab job={job} />
        </TabsContent>

        <TabsContent value="activity">
          <ActivityTab job={job} />
        </TabsContent>

        <TabsContent value="analytics">
          <AnalyticsTab job={job} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default JobDetail;
